import React, { useState } from 'react';
import { setupAPI } from '../services/api';
import PasswordRequirements from '../components/PasswordRequirements';
import { 
  Rocket, 
  User, 
  Home, 
  Mail, 
  ChevronRight, 
  ChevronLeft,
  Check,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Server,
  Shield
} from 'lucide-react';
import '../styles/SetupWizard.css';

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Rocket },
  { id: 'admin', title: 'Admin Account', icon: User },
  { id: 'household', title: 'Household', icon: Home },
  { id: 'smtp', title: 'Email', icon: Mail },
  { id: 'complete', title: 'Complete', icon: Check }
];

function SetupWizard({ onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Form data - all fields start empty
  const [formData, setFormData] = useState({
    admin: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: ''
    },
    household: {
      enabled: false,
      name: ''
    },
    smtp: {
      enabled: false,
      host: '',
      port: 587,
      secure: false,
      authUser: '',
      authPass: '',
      fromEmail: '',
      fromName: 'Remidy'
    }
  });

  // Validation
  const [validation, setValidation] = useState({});

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    // Clear validation error for this field
    setValidation(prev => ({
      ...prev,
      [`${section}.${field}`]: null
    }));
  };

  const validateStep = () => {
    const errors = {};
    
    if (currentStep === 1) { // Admin step
      if (!formData.admin.email) {
        errors['admin.email'] = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.admin.email)) {
        errors['admin.email'] = 'Invalid email format';
      }
      if (!formData.admin.password) {
        errors['admin.password'] = 'Password is required';
      } else if (formData.admin.password.length < 8) {
        errors['admin.password'] = 'Password must be at least 8 characters';
      }
      if (formData.admin.password !== formData.admin.confirmPassword) {
        errors['admin.confirmPassword'] = 'Passwords do not match';
      }
      if (!formData.admin.firstName) {
        errors['admin.firstName'] = 'First name is required';
      }
      if (!formData.admin.lastName) {
        errors['admin.lastName'] = 'Last name is required';
      }
    }
    
    if (currentStep === 2) { // Household step (only if enabled)
      if (formData.household.enabled && !formData.household.name) {
        errors['household.name'] = 'Household name is required when enabled';
      }
    }
    
    if (currentStep === 3 && formData.smtp.enabled) { // SMTP step (only if enabled)
      if (!formData.smtp.host) {
        errors['smtp.host'] = 'SMTP host is required';
      }
      if (!formData.smtp.authUser) {
        errors['smtp.authUser'] = 'Username is required';
      }
      if (!formData.smtp.authPass) {
        errors['smtp.authPass'] = 'Password is required';
      }
      if (!formData.smtp.fromEmail) {
        errors['smtp.fromEmail'] = 'From email is required';
      }
    }
    
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1));
      setError('');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setError('');
  };

  const completeSetup = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await setupAPI.complete({
        admin: {
          email: formData.admin.email,
          password: formData.admin.password,
          firstName: formData.admin.firstName,
          lastName: formData.admin.lastName
        },
        household: formData.household.enabled ? { name: formData.household.name } : null,
        smtp: formData.smtp.enabled ? formData.smtp : null
      });

      // Store auth data
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('household', JSON.stringify(response.data.household));

      // Move to complete step
      setCurrentStep(STEPS.length - 1);

      // Notify parent after a brief delay to show completion
      setTimeout(() => {
        onComplete(response.data.user, response.data.household);
      }, 2000);

    } catch (err) {
      console.error('Setup error:', err);
      setError(err.response?.data?.error || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (STEPS[currentStep].id) {
      case 'welcome':
        return <WelcomeStep />;
      case 'admin':
        return (
          <AdminStep
            data={formData.admin}
            onChange={(field, value) => updateFormData('admin', field, value)}
            validation={validation}
            showPassword={showPassword}
            setShowPassword={setShowPassword}
            showConfirmPassword={showConfirmPassword}
            setShowConfirmPassword={setShowConfirmPassword}
          />
        );
      case 'household':
        return (
          <HouseholdStep
            data={formData.household}
            onChange={(field, value) => updateFormData('household', field, value)}
            validation={validation}
          />
        );
      case 'smtp':
        return (
          <SmtpStep
            data={formData.smtp}
            onChange={(field, value) => updateFormData('smtp', field, value)}
            validation={validation}
            showPassword={showSmtpPassword}
            setShowPassword={setShowSmtpPassword}
          />
        );
      case 'complete':
        return <CompleteStep />;
      default:
        return null;
    }
  };

  const isLastSetupStep = currentStep === STEPS.length - 2; // SMTP is last setup step
  const isCompleteStep = currentStep === STEPS.length - 1;

  return (
    <div className="setup-wizard">
      <div className="setup-container">
        {/* Progress Steps */}
        <div className="setup-progress">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = index === currentStep;
            const isCompleted = index < currentStep;
            
            return (
              <div 
                key={step.id} 
                className={`progress-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              >
                <div className="step-icon">
                  {isCompleted ? <Check size={20} /> : <Icon size={20} />}
                </div>
                <span className="step-title">{step.title}</span>
              </div>
            );
          })}
        </div>

        {/* Step Content */}
        <div className="setup-content">
          {error && (
            <div className="setup-error">
              <AlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}
          
          {renderStepContent()}
        </div>

        {/* Navigation */}
        {!isCompleteStep && (
          <div className="setup-navigation">
            {currentStep > 0 && (
              <button 
                className="btn-secondary"
                onClick={prevStep}
                disabled={loading}
              >
                <ChevronLeft size={20} />
                Back
              </button>
            )}
            
            <div className="nav-spacer" />
            
            {currentStep === 0 ? (
              <button 
                className="btn-primary"
                onClick={nextStep}
              >
                Get Started
                <ChevronRight size={20} />
              </button>
            ) : isLastSetupStep ? (
              <button 
                className="btn-primary"
                onClick={completeSetup}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={20} className="spinner" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <Check size={20} />
                  </>
                )}
              </button>
            ) : (
              <button 
                className="btn-primary"
                onClick={nextStep}
              >
                Continue
                <ChevronRight size={20} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Step Components
function WelcomeStep() {
  return (
    <div className="step-welcome">
      <div className="welcome-icon">
        <Rocket size={64} />
      </div>
      <h1>Welcome to Remidy</h1>
      <p className="welcome-subtitle">
        Let's get your application set up in just a few steps.
      </p>
      <div className="welcome-features">
        <div className="feature">
          <Shield size={24} />
          <div>
            <h3>Create Admin Account</h3>
            <p>Set up your administrator credentials</p>
          </div>
        </div>
        <div className="feature">
          <Home size={24} />
          <div>
            <h3>Set Up Household</h3>
            <p>Create your first household to manage bills</p>
          </div>
        </div>
        <div className="feature">
          <Mail size={24} />
          <div>
            <h3>Configure Email</h3>
            <p>Enable email notifications and reminders</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminStep({ data, onChange, validation, showPassword, setShowPassword, showConfirmPassword, setShowConfirmPassword }) {
  return (
    <div className="step-form">
      <h2>Create Admin Account</h2>
      <p className="step-description">
        This will be the primary administrator account with full access to all features.
      </p>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            id="firstName"
            value={data.firstName}
            onChange={(e) => onChange('firstName', e.target.value)}
            placeholder="John"
            className={validation['admin.firstName'] ? 'error' : ''}
          />
          {validation['admin.firstName'] && (
            <span className="field-error">{validation['admin.firstName']}</span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            id="lastName"
            value={data.lastName}
            onChange={(e) => onChange('lastName', e.target.value)}
            placeholder="Doe"
            className={validation['admin.lastName'] ? 'error' : ''}
          />
          {validation['admin.lastName'] && (
            <span className="field-error">{validation['admin.lastName']}</span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <input
          type="email"
          id="email"
          value={data.email}
          onChange={(e) => onChange('email', e.target.value)}
          placeholder="admin@example.com"
          className={validation['admin.email'] ? 'error' : ''}
        />
        {validation['admin.email'] && (
          <span className="field-error">{validation['admin.email']}</span>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={data.password}
              onChange={(e) => onChange('password', e.target.value)}
              placeholder="Enter password"
              className={validation['admin.password'] ? 'error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {validation['admin.password'] && (
            <span className="field-error">{validation['admin.password']}</span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="confirmPassword">Confirm Password</label>
          <div className="password-input">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={data.confirmPassword}
              onChange={(e) => onChange('confirmPassword', e.target.value)}
              placeholder="Confirm password"
              className={validation['admin.confirmPassword'] ? 'error' : ''}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {validation['admin.confirmPassword'] && (
            <span className="field-error">{validation['admin.confirmPassword']}</span>
          )}
        </div>
      </div>

      <PasswordRequirements password={data.password} showAlways={true} />
    </div>
  );
}

function HouseholdStep({ data, onChange, validation }) {
  return (
    <div className="step-form">
      <h2>Create Your Household</h2>
      <p className="step-description">
        A household is where you'll manage your bills. You can skip this and create one later.
      </p>

      <div className="smtp-toggle">
        <label className="toggle-container">
          <input
            type="checkbox"
            checked={data.enabled}
            onChange={(e) => onChange('enabled', e.target.checked)}
          />
          <span className="toggle-switch"></span>
          <span className="toggle-label">Create a Household Now</span>
        </label>
      </div>

      {data.enabled && (
        <div className="smtp-fields">
          <div className="form-group">
            <label htmlFor="householdName">Household Name</label>
            <input
              type="text"
              id="householdName"
              value={data.name}
              onChange={(e) => onChange('name', e.target.value)}
              placeholder="Smith Family"
              className={validation['household.name'] ? 'error' : ''}
            />
            {validation['household.name'] && (
              <span className="field-error">{validation['household.name']}</span>
            )}
            <p className="field-hint">
              This could be your family name, apartment number, or any identifier you prefer.
            </p>
          </div>
        </div>
      )}

      {!data.enabled && (
        <div className="skip-notice">
          <Home size={24} />
          <div>
            <h4>Skip for now</h4>
            <p>You can create or join a household later from the Household page.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function SmtpStep({ data, onChange, validation, showPassword, setShowPassword }) {
  return (
    <div className="step-form">
      <h2>Email Configuration</h2>
      <p className="step-description">
        Configure email to enable notifications, reminders, and password resets.
        You can skip this and configure it later in Admin settings.
      </p>

      <div className="smtp-toggle">
        <label className="toggle-container">
          <input
            type="checkbox"
            checked={data.enabled}
            onChange={(e) => onChange('enabled', e.target.checked)}
          />
          <span className="toggle-switch"></span>
          <span className="toggle-label">Enable Email Notifications</span>
        </label>
      </div>

      {data.enabled && (
        <div className="smtp-fields">
          <div className="form-row">
            <div className="form-group flex-2">
              <label htmlFor="smtpHost">SMTP Host</label>
              <input
                type="text"
                id="smtpHost"
                value={data.host}
                onChange={(e) => onChange('host', e.target.value)}
                placeholder="smtp.gmail.com"
                className={validation['smtp.host'] ? 'error' : ''}
              />
              {validation['smtp.host'] && (
                <span className="field-error">{validation['smtp.host']}</span>
              )}
            </div>
            <div className="form-group flex-1">
              <label htmlFor="smtpPort">Port</label>
              <select
                id="smtpPort"
                value={data.port}
                onChange={(e) => onChange('port', parseInt(e.target.value))}
              >
                <option value={25}>25 (SMTP)</option>
                <option value={465}>465 (SMTPS)</option>
                <option value={587}>587 (Submission)</option>
                <option value={2525}>2525 (Alt)</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="toggle-container inline">
              <input
                type="checkbox"
                checked={data.secure}
                onChange={(e) => onChange('secure', e.target.checked)}
              />
              <span className="toggle-switch small"></span>
              <span className="toggle-label">Use TLS/SSL encryption</span>
            </label>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="authUser">Username / Email</label>
              <input
                type="text"
                id="authUser"
                value={data.authUser}
                onChange={(e) => onChange('authUser', e.target.value)}
                placeholder="your-email@gmail.com"
                className={validation['smtp.authUser'] ? 'error' : ''}
              />
              {validation['smtp.authUser'] && (
                <span className="field-error">{validation['smtp.authUser']}</span>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="authPass">Password / App Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="authPass"
                  value={data.authPass}
                  onChange={(e) => onChange('authPass', e.target.value)}
                  placeholder="••••••••"
                  className={validation['smtp.authPass'] ? 'error' : ''}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {validation['smtp.authPass'] && (
                <span className="field-error">{validation['smtp.authPass']}</span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="fromName">From Name</label>
              <input
                type="text"
                id="fromName"
                value={data.fromName}
                onChange={(e) => onChange('fromName', e.target.value)}
                placeholder="Remidy"
              />
            </div>
            <div className="form-group">
              <label htmlFor="fromEmail">From Email</label>
              <input
                type="email"
                id="fromEmail"
                value={data.fromEmail}
                onChange={(e) => onChange('fromEmail', e.target.value)}
                placeholder="noreply@example.com"
                className={validation['smtp.fromEmail'] ? 'error' : ''}
              />
              {validation['smtp.fromEmail'] && (
                <span className="field-error">{validation['smtp.fromEmail']}</span>
              )}
            </div>
          </div>
        </div>
      )}

      {!data.enabled && (
        <div className="skip-notice">
          <Server size={24} />
          <div>
            <h4>Skip for now</h4>
            <p>You can configure email settings later in the Admin panel under Email / SMTP settings.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function CompleteStep() {
  return (
    <div className="step-complete">
      <div className="complete-icon">
        <Check size={64} />
      </div>
      <h1>Setup Complete!</h1>
      <p>Your Remidy is ready to use.</p>
      <div className="loading-redirect">
        <Loader2 size={24} className="spinner" />
        <span>Redirecting to dashboard...</span>
      </div>
    </div>
  );
}

export default SetupWizard;
